import {
    createRouter,
    createWebHistory,
    type RouteLocationNormalized,
    type RouteLocationRaw,
} from 'vue-router';
import AuthenticationUtility from '@/views/AuthenticationUtility.vue';
import { useUserStore } from '@/stores/user';
import fsumRoutes from '@/router/fsum';
import adminCenterRoutes from '@/router/adminCenter';
import { useSchoolStore } from '@/stores/school';
import * as routeHelpers from '@/functions/routeHelpers';
import { availableParts, type AvailableParts } from '@/types/Certificate';
import { usePartStore } from '@/stores/part';

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        ...fsumRoutes,
        ...adminCenterRoutes,
        {
            path: '/login',
            name: 'authentication',
            component: AuthenticationUtility,
        },
    ],
});

/* Can probably be combined somehow with the school setup in guardAuthentication, but this doesn't
 * want any of the default behavior.
 */
const setupAdminSchool = async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
): Promise<Error | RouteLocationRaw | boolean> => {
    const userStore = useUserStore();

    if (userStore.user === null) {
        return { name: 'authentication' };
    }

    if (to.name === 'lockoutReport') {
        if (!userStore.isGlobalAdmin) {
            return routeHelpers.notifyAndHandleNoAccess(from);
        }
    }

    const schoolStore = useSchoolStore();
    if (schoolStore.schools.length === 0) {
        await schoolStore.setupForUserId(userStore.user.id);
    }

    const canAccessAdminCenter = userStore.isGlobalAdmin || userStore.isSchoolOwner;
    if (!to.params.schoolSlug && canAccessAdminCenter) {
        return true;
    }

    const schoolFromSlug = schoolStore.getSchoolByUrlSlug(to.params.schoolSlug as string);
    if (schoolFromSlug === null) {
        return canAccessAdminCenter
            ? routeHelpers.notifyAndHandleNoAccess({ name: 'schoolDirectory' })
            : routeHelpers.notifyAndHandleNoAccess();
    }

    schoolStore.setSchool(schoolFromSlug);

    return true;
};

const guardAuthentication = async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
): Promise<Error | RouteLocationRaw | boolean> => {
    const userStore = useUserStore();

    if (to.name !== 'authentication' && !userStore.isAuthenticated()) {
        return { name: 'authentication' };
    }

    if (to.meta.adminCenter) {
        return setupAdminSchool(to, from);
    }

    // Set up user's schools, attempt to route to requested school and course
    if (userStore.user !== null && to.meta.requiresSchool) {
        if (routeHelpers.hasSameRouteParams(to, from, ['schoolSlug', 'part', 'courseAcronym'])) {
            return true;
        }

        const schoolStore = useSchoolStore();

        // Load schools if not already loaded or if the requested school isn't found
        if (schoolStore.schools.length === 0) {
            await schoolStore.setupForUserId(userStore.user.id);
        }

        const school = routeHelpers.selectSchoolFromRoute(schoolStore, to.params.schoolSlug as string);
        if (school === null) {
            return routeHelpers.notifyAndHandleNoAccess(from);
        }

        // Go to 404 page if the part is provided and not valid
        const partStore = usePartStore();
        const partFromStore = partStore.getPartOrUseDefault(school);
        const partFromRoute = Number(to.params.part) as AvailableParts;
        const isPartFromRouteInvalid = Number.isNaN(partFromRoute);
        const part = isPartFromRouteInvalid ? partFromStore : partFromRoute;

        if (!availableParts.includes(part)) {
            return { name: 'NotFound' };
        }

        if (!isPartFromRouteInvalid) {
            partStore.setPart(partFromRoute);
        }

        const selectedRatingAcronym = to.params.ratingAcronym as string;

        const course = routeHelpers.selectCourseFromRoute(
            school,
            to.params.courseAcronym as string,
            part,
        );

        if (course === undefined) {
            return routeHelpers.notifyAndHandleNoAccess(from);
        }

        // Go to 404 page if the rating is provided and not valid
        if (selectedRatingAcronym && !course.ratings.find(
            (rating) => rating.acronym.toLowerCase() === selectedRatingAcronym?.toLowerCase(),
        )) {
            return { name: 'NotFound' };
        }

        /**
         * Handle any exceptions to the default behavior of setting the school and course
         */
        if (to.name === 'default' || to.name == null) {
            return {
                name: 'classOverview',
                params: {
                    schoolSlug: school.urlSlug,
                    courseAcronym: course.certificate.acronym?.toLowerCase(),
                    part,
                    ratingAcronym: selectedRatingAcronym
                        ? selectedRatingAcronym.toLowerCase()
                        : course.ratings[0]?.acronym.toLowerCase(),
                },
            };
        }

        if (to.meta.slugless) {
            if (!to.meta.parts?.includes(part)) {
                return { name: 'default' };
            }

            /*
             * Don't change the school or course if the user is on a slugless route
             * and the school and course have already been set up
             */
            if (schoolStore.school && schoolStore.course) {
                return true;
            }
        }

        // User can access the requested school and course
        schoolStore.setSchool(school);
        schoolStore.course = course;
    }

    return true;
};

/* eslint-disable consistent-return */
router.beforeEach(guardAuthentication);

export default router;
